import { SESSION_TYPE } from '../constants/sessionType';

/** Canonical challenge type consumed by `ChallengeTypeTag`. */
export type ChallengeType = 'new' | 'review' | 'combo';

/** Origin of a practice session shown by `PracticeOriginLabel`. */
export type PracticeOrigin = 'student' | 'tutor';

/**
 * Maps the raw content type coming from the API (e.g. "NEW", "REINFORCEMENT",
 * "COMBO") to the canonical challenge type consumed by `ChallengeTypeTag`.
 *
 * This is the single source of truth for the mapping so the practice view and
 * the student recommendations always render the same tag for the same session.
 */
export function mapContentTypeToChallenge(contentType?: string): ChallengeType {
  const typeMap: Record<string, ChallengeType> = {
    NEW: 'new',
    REINFORCEMENT: 'review',
    COMBO: 'combo',
  };

  if (!contentType) return 'new';

  return typeMap[contentType] ?? 'new';
}

/**
 * Maps the raw session type coming from the API (e.g. "NEW", "PRACTICE",
 * "STUDENT") to the practice origin shown on the recommendation cards:
 * student-created sessions render "Created by you", everything else renders
 * "From your tutor".
 *
 * INTENTIONAL: a volatile single-KC practice (`KC`) also maps to `'tutor'`, so its header
 * reads "✨ Del tutor" — that is exactly what the TANAI-2087 design shows, because the
 * practice is prepared by the tutor from the student's blocked KC rather than picked by the
 * student. Do not special-case `KC` here: what changes for those sessions is the scope chip
 * next to this label (`PracticeScopeTag`), not the origin.
 */
export function mapSessionTypeToOrigin(sessionType?: string): PracticeOrigin {
  return sessionType === SESSION_TYPE.STUDENT ? 'student' : 'tutor';
}

/**
 * Whether the session is a volatile single-KC practice launched from "Áreas de mejora"
 * (TANAI-2087). Those sessions are the only ones that swap the challenge tag for the
 * "Áreas de mejora" scope chip, confirm the exit with a single-CTA dialog and return to
 * "Mi progreso" instead of "Today". Every other raw type (`NEW`, `PRACTICE`, `STUDENT`,
 * `TEACHER`) — and a session that arrives without a type — is not a KC practice.
 *
 * Single source of truth for the check so the header, the exit flow and the exit dialog can
 * never disagree about what a KC practice is.
 */
export function isKcPracticeSession(sessionType?: string): boolean {
  return sessionType === SESSION_TYPE.KC;
}
